import React, { useState, useEffect } from "react";
import { Tooltip } from "@material-ui/core";
import { SupervisorAccountRounded as SupervisorAccountIcon } from "@material-ui/icons";
import { Query, Column } from "material-table";
import { useSnackbar } from "notistack";
import { Membership } from "api/memberships";
import { DefaultTable } from "components";
import { Member, getMembers, patchMembers, deleteMembers, postMembersExport } from "api/admin/members";
import { useMounted } from "utils/hooks";
import i18n from "./i18n";
import BulkEditDialog from "./MembersBulkEditDialog";

// prop types
interface ManageMembersTabProps {
    clubId?: string,
    memberships: Membership[],
}

// admin members table
const ManageMembersTab = ({ clubId, memberships }: ManageMembersTabProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [isLoading, setLoading] = useState(false);
    const [showBulkEditDialog, setShowBulkEditDialog] = useState(false);
    // const [showCreatePostDialogue, setShowCreatePostDialogue] = useState(false);

    // convert material-table query to API params
    const convertQueryToParams = (query: Query<Member>) => {
        // paging
        const params: Parameters<typeof getMembers>[0] = {
            limit: query.pageSize,
            offset: query.pageSize * query.page,
        };

        // sorting
        if (query.orderBy) {
            params.sortOrder = query.orderDirection.toUpperCase() as typeof params["sortOrder"];
            params.sortKey = query.orderBy.field as typeof params["sortKey"];
        }

        // filter
        params.query = query.search;
        params.clubId = clubId;

        return params;
    };

    const clubIdToShortName = memberships.reduce<{ [id: string]: string }>((acc, elem) => {
        acc[elem.club.id] = elem.club.shortName;
        return acc;
    }, {});

    // update row handler (update member)
    const onRowUpdate = (newData: Member) => new Promise<void>((resolve) => {
        patchMembers({
            isBlockedForBookings: newData.isBlockedForBookings,
            membershipIds: [newData.membershipId],
            userRole: newData.userRole,
            site: newData.site,
        }).then(() => resolve()).catch((err) => { displayError(err); resolve(); });
    });

    // delete row handler (delete member)
    const onRowDelete = (oldData: Member) => new Promise<void>((resolve) => {
        deleteMembers({
            membershipIds: [oldData.membershipId],
        }).then(() => resolve()).catch((err) => { displayError(err); resolve(); });
    });

    // const postStoryHandler = () => {
    //     setShowCreatePostDialogue(!showCreatePostDialogue);
    // };

    // export action
    const exportBookings = async () => {
        setLoading(true);
        try {
            const { url } = await postMembersExport({ clubId });
            window.open(url);
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // table actions
    const isOwner = memberships.find((m) => m.club.id === clubId)?.userRole === "owner";
    const isB2B = memberships.find((m) => m.club.id === clubId)?.club.capabilities.includes("company");

    const tableActions = [
        {
            icon: "gavel",
            isFreeAction: true,
            tooltip: i18n.bulkEdit,
            onClick: () => setShowBulkEditDialog(true),
        },
        // {
        //     icon: "note_add",
        //     isFreeAction: true,
        //     tooltip: Localizer.get("news"),
        //     onClick: () => postStoryHandler(),
        // },
        {
            disabled: !isOwner,
            icon: "save_alt",
            isFreeAction: true,
            tooltip: isOwner ? i18n.export : i18n.exportOwnerOnly,
            onClick: exportBookings,
        },
    ];

    // render alias account details
    const renderAlias = (user: Member) => (
        <>
            <Tooltip title={`${i18n.aliasAccount} -> ${user.mainAccount!.firstName} ${user.mainAccount!.lastName} (${user.mainAccount!.email})`}>
                <div>
                    <SupervisorAccountIcon fontSize="small" />
                    {`(${user.email})`}
                </div>
            </Tooltip>
        </>
    );

    const userRoleB2C = {
        field: "userRole",
        lookup: ["standard", "trainer", "groundskeeper", "admin"].reduce<{ [role: string]: string }>((acc, elem) => {
            acc[elem] = i18n.userRolesB2C[elem];
            return acc;
        }, {}),
        render: (rowData: Member) => i18n.userRolesB2C[rowData.userRole],
        sorting: false,
        title: i18n.status,
    };
    const userRoleB2B = {
        field: "userRole",
        lookup: ["standard", "trainer", "admin"].reduce<{ [role: string]: string }>((acc, elem) => {
            acc[elem] = i18n.userRolesB2B[elem];
            return acc;
        }, {}),
        render: (rowData: Member) => i18n.userRolesB2B[rowData.userRole],
        sorting: false,
        title: i18n.status,
    };

    let displayUserRole = {};
    if (isB2B) {
        displayUserRole = { ...userRoleB2B };
    } else {
        displayUserRole = { ...userRoleB2C };
    }

    // table columns
    const tableColumns: Column<Member>[] = [
        {
            editable: "never",
            field: "firstName",
            title: i18n.firstName,
        },
        {
            editable: "never",
            field: "lastName",
            title: i18n.lastName,
        },
        {
            editable: "never",
            field: "email",
            render: (rowData: Member) => (!rowData.isAlias ? rowData.email : renderAlias(rowData)),
            sorting: false,
            title: i18n.emailAddress,
        },
        {
            editable: "never",
            field: "clubId",
            lookup: clubIdToShortName,
            sorting: false,
            title: i18n.club,
        },
        displayUserRole,
        {
            field: "isBlockedForBookings",
            lookup: {
                true: i18n.blocked,
                false: i18n.allowed,
            },
            sorting: false,
            title: i18n.bookings,
        },
        {
            field: "site",
            sorting: false,
            title: i18n.site,
        },
    ];

    // is not an owner
    const isNoOwner = ({ userRole }: Member) => userRole !== "owner";

    // refresh (-> remote data)
    const tableRef = React.useRef<any>(null);
    const isMounted = useMounted();
    const refreshMembersTable = () => {
        if (!isMounted) return; // skip on initial material-table fetch
        if (tableRef.current) tableRef.current.onQueryChange(); // execute query again
    };
    useEffect(refreshMembersTable, [clubId]);

    const defaultData = {
        data: [],
        page: 0,
        totalCount: 0,
    };

    // render
    return (
        <>
            {/* members table */}
            <DefaultTable
                actions={tableActions}
                columns={tableColumns}
                data={(query) => new Promise((resolve) => {
                    const params = convertQueryToParams(query);
                    getMembers(params)
                        .then((response) => {
                            resolve({
                                ...response,
                                page: query.page,
                            });
                        })
                        .catch((error) => {
                            displayError(error);
                            resolve(defaultData);
                        });
                })}
                editable={{
                    isDeletable: isNoOwner,
                    isEditable: isNoOwner,
                    onRowDelete,
                    onRowUpdate,
                }}
                isLoading={isLoading}
                tableRef={tableRef}
                title={i18n.members}
            />

            {/* dialogs */}
            <BulkEditDialog
                showDialog={showBulkEditDialog}
                onClose={() => setShowBulkEditDialog(false)}
                onRefreshMembersTable={refreshMembersTable}
                isB2B={isB2B}
            />

            {/* {showCreatePostDialogue && (
                <CreateStoryDialogue
                    isOpen={showCreatePostDialogue}
                    handleClose={() => setShowCreatePostDialogue(!showCreatePostDialogue)}
                    clubId={props.currentClub.id}
                    clubList={props.clubMemberships}
                />
            )} */}
        </>
    );
};

export default ManageMembersTab;
